import { useEffect, useState } from 'react';
import { ListConductor } from '../../list-conductor/list-conductor';
import { FilterList } from '../../list-conductor/list-actions';
import { AppliedFilters } from '../interface';
import { FilterField, ListConductorFilters } from '../../list-conductor/filters';
import { FILTER_OPERATOR_SANITIZER_MAP } from '../helpers';

const DEFAULT_NUMERICAL_OPERATOR = 'EQUALS';

export function useFilters(listConductor: ListConductor) {
  const [filters, setFilters] = useState<ListConductorFilters>([]);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});

  useEffect(() => {
    setAppliedFilters({ ...getAppliedFilters(filters) });
  }, [filters]);

  const sanitizeAndSetFilterData = (filterData: FilterField) => {
    // check if filter value is empty and remove it from filters

    const filterIndex = filters.findIndex(data => data.filter === filterData.filter);
    if (filterIndex > -1) {
      setFilters((prev: ListConductorFilters) => {
        const newFilters = [...prev];
        newFilters[filterIndex] = { ...filterData };
        return [...newFilters];
      });
    } else {
      setFilters(prev => [...prev, filterData]);
    }
  };

  const setFilter = (filter: {
    filterKey: string;
    operator?: string;
    value: Omit<FilterField, 'filter'> | Record<string, string> | unknown;
  }) => {
    const { filterKey, value } = filter;
    let _filter: FilterField;
    if (typeof value === 'object' && value && !('operator' in value)) {
      _filter = {
        filter: filterKey,
        operator: Array.isArray(value) ? 'IN' : DEFAULT_NUMERICAL_OPERATOR,
        value
      };
    } else {
      _filter = {
        filter: filterKey,
        operator: (value as Record<string, unknown>).operator as string,
        value: (value as Record<string, unknown>).value
      };
    }

    sanitizeAndSetFilterData(_filter);
  };

  const setFiltersFromListMeta = (listMetaFilters: ListConductorFilters) => {
    setFilters(listMetaFilters);
  };

  const clearAllFilters = () => {
    setFilters([]);
  };
  const clearFilter = (filterKey: string) => {
    setFilters(prev => {
      const _filters = prev;
      const filterIndex = prev.findIndex(data => data.filter === filterKey);
      if (filterIndex > -1) {
        _filters[filterIndex].value = null;
      }
      return [..._filters];
    });
  };
  const applyFilters = () => {
    const sanitizedFilters = filters.map(filter => {
      const valueSanitizer = FILTER_OPERATOR_SANITIZER_MAP[filter.operator];
      if (valueSanitizer && filter.value) {
        // eslint-disable-next-line no-param-reassign
        filter.value = valueSanitizer(filter.value);
      }
      return filter;
    });
    listConductor.dispatch(new FilterList(sanitizedFilters));
  };

  const getAppliedFilters = (filtersData: ListConductorFilters) => {
    const _appliedFilters: AppliedFilters = {};
    filtersData.forEach(({ filter, operator, value }) => {
      _appliedFilters[filter] = {
        operator,
        value
      };
    });
    return _appliedFilters;
  };

  return {
    setFilter,
    applyFilters,
    appliedFilters,
    clearAllFilters,
    setFiltersFromListMeta,
    clearFilter
  };
}
